/* eslint-disable max-len */
/* eslint-disable max-lines */

// Shared eSIM status/stage helpers.
//
// Two layers live here:
//  1. The legacy profile-status helpers (Active/Inactive/Deleted/Provisioned/"BPP installation"),
//     keyed off the raw `esimProfileStatus` string the reservations list + profile header use.
//     Kept verbatim so those views are unchanged.
//  2. The richer installation-STAGE model used by the diagnostics panel, keyed off the derived
//     stage returned by GET /v1/Esim/Profile/{id}/Diagnostics (enum names from the backend).

// ---- Legacy profile status (raw DB status string) ----

export const ESIM_STATUS_KEYS = [
  "Active",
  "Inactive",
  "Deleted",
  "Provisioned",
  "BPP installation"
];

export const getEsimProfileStatusLabel = (status: string) => {
  switch (status) {
    case "Provisioned":
      return "Gati për instalim";
    case "Deleted":
      return "I fshirë";
    case "Active":
      return "Aktiv";
    case "Inactive":
      return "Jo aktiv";
    case "BPP installation":
      return "Duke instaluar";
    default:
      return status;
  }
};

export const getEsimProfileStatusColor = (status: string) => {
  switch (status) {
    case "Provisioned":
      return "bg-blue text-white";
    case "Deleted":
      return "bg-red text-white";
    case "Active":
      return "bg-green text-white";
    case "Inactive":
      return "bg-gray text-white";
    case "BPP installation":
      return "bg-secondary text-white";
    default:
      return "bg-primary text-white";
  }
};

export const getEsimProfileStatusDescription = (status: string) => {
  switch (status) {
    case "Active":
      return "Klienti e ka instaluar dhe aktivizuar eSIM-in. Nëse shërbimi ende nuk funksionon, sigurohuni që Data Roaming është i ndezur në pajisje.";
    case "Inactive":
      return "Klienti e ka instaluar eSIM-in, por nuk e ka aktivizuar për përdorim të internetit, pasi nuk e ka ndezur opsionin “Turn on this line” në Settings e pajisjes.";
    case "Deleted":
      return "Klienti e ka fshirë eSIM-in nga pajisja e tij.";
    case "Provisioned":
      return "Klienti e ka blerë eSIM-in, por ende nuk ka filluar asnjë hap të procesit të instalimit apo aktivizimit.";
    case "BPP installation":
      return "Klienti është në proces të instalimit të eSIM-it, por instalimi ende nuk ka përfunduar.";
    default:
      return "";
  }
};

// ---- Installation stage (derived by the backend diagnostics endpoint) ----

export type EsimStage =
  | "Unknown"
  | "Purchased"
  | "ProfileReleased"
  | "DownloadedInstalled"
  | "Enabled"
  | "ConnectedToNetwork"
  | "UsingData"
  | "NeverConnected"
  | "Disabled"
  | "Deleted";

// Ordered happy path, drives the stepper. Terminal/problem states are not on the rail.
export const ESIM_STAGE_ORDER: EsimStage[] = [
  "Purchased",
  "ProfileReleased",
  "DownloadedInstalled",
  "Enabled",
  "ConnectedToNetwork",
  "UsingData"
];

export const isHappyPathStage = (stage: string): boolean =>
  ESIM_STAGE_ORDER.includes(stage as EsimStage);

export const getEsimStageLabel = (stage: string): string => {
  switch (stage) {
    case "Purchased":
      return "Blerë";
    case "ProfileReleased":
      return "Gati për instalim";
    case "DownloadedInstalled":
      return "Duke instaluar";
    case "Enabled":
      return "Linja e ndezur";
    case "ConnectedToNetwork":
      return "Lidhur me rrjetin";
    case "UsingData":
      return "Aktiv";
    case "NeverConnected":
      return "S'është lidhur kurrë";
    case "Disabled":
      return "Çaktivizuar";
    case "Deleted":
      return "I fshirë";
    default:
      return "E panjohur";
  }
};

export const getEsimStageColor = (stage: string): string => {
  switch (stage) {
    case "UsingData":
    case "ConnectedToNetwork":
      return "bg-green text-white";
    case "Enabled":
    case "DownloadedInstalled":
      return "bg-secondary text-white";
    case "ProfileReleased":
    case "Purchased":
      return "bg-blue text-white";
    case "NeverConnected":
    case "Disabled":
      return "bg-gray text-white";
    case "Deleted":
      return "bg-red text-white";
    default:
      return "bg-primary text-white";
  }
};

export const getEsimStageDescription = (stage: string): string => {
  switch (stage) {
    case "Purchased":
      return "Klienti e ka blerë eSIM-in, por ende nuk ka filluar instalimin.";
    case "ProfileReleased":
      return "eSIM-i është gati. Klienti duhet ta instalojë në pajisje.";
    case "DownloadedInstalled":
      return "eSIM-i po shkarkohet/instalohet në pajisje. Instalimi ende nuk ka përfunduar.";
    case "Enabled":
      return "Linja është e ndezur, por pajisja ende nuk është lidhur me rrjetin.";
    case "ConnectedToNetwork":
      return "Pajisja u lidh me rrjetin e operatorit, por ende nuk ka përdorur të dhëna. Sigurohuni që Data Roaming është i ndezur në pajisje.";
    case "UsingData":
      return "eSIM-i është aktiv dhe po përdor të dhëna normalisht.";
    case "NeverConnected":
      return "eSIM-i u instalua, por nuk u lidh kurrë me rrjetin. Kontrolloni që te pajisja janë ndezur 'Turn on this line' dhe 'Data Roaming'.";
    case "Disabled":
      return "Linja e eSIM-it është e fikur në pajisje. Klienti duhet ta ndezë sërish.";
    case "Deleted":
      return "Klienti e ka fshirë eSIM-in nga pajisja.";
    default:
      return "Faza e instalimit nuk u përcaktua dot.";
  }
};
